// Lo PURO de los PDF en el chat: cómo el agente los ENTIENDE y cómo escribe uno nuevo —
// ADR-2026-09-08-el-agente-lee-y-crea-pdfs.
//
// Mismo problema que con las planillas: un PDF de 60 páginas no entra en un turno. Al
// adjuntarlo viaja su FORMA (cuántas páginas, si tiene texto) y las primeras; el resto se
// pide con `ver_pdf`.
//
// Y una diferencia importante: si el PDF está ESCANEADO no hay texto que mandar. Ahí
// `ver_pdf` devuelve las páginas RENDERIZADAS como imágenes y el agente las mira. Es más
// caro en tokens, así que van de a pocas.

import type { Alineacion, BloquePdf } from '../../../utils/pdf/escribirPdf';

export interface Pdf {
  nombre: string;
  paginas: number;
  textoPorPagina: string[];
  /** false = escaneado: hay que mirarlo como imagen. */
  tieneTexto: boolean;
  /** Los bytes originales, para poder renderizar páginas después. */
  bytes: Uint8Array;
}

/** Caracteres de muestra que viajan al adjuntar. Un par de páginas de texto. */
export const CARACTERES_MUESTRA = 3_000;
/** Páginas por llamada a `ver_pdf` cuando hay texto. */
export const MAX_PAGINAS_TEXTO = 10;
/** Páginas por llamada cuando hay que RENDERIZAR. Cada una es una imagen en el turno y en
 *  todo el historial siguiente: por eso son muchas menos. */
export const MAX_PAGINAS_IMAGEN = 3;
/** Tope de páginas que se leen de un PDF adjunto. */
export const MAX_PAGINAS_LEIDAS = 200;

const texto = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

const entero = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * Lo que viaja en el turno al adjuntar. Con texto: la forma y el principio. Sin texto: la
 * forma y la instrucción de mirarlo, que es lo único que se puede hacer.
 */
export function resumenDePdf(p: Pdf): string {
  const cabeza = `[PDF adjunto: ${p.nombre}] ${p.paginas} página(s).`;
  if (!p.tieneTexto) {
    return (
      `${cabeza} ESCANEADO: no tiene capa de texto, así que no hay nada que leer como texto. ` +
      `Para entenderlo llamá ver_pdf: te devuelve las páginas como IMÁGENES y las mirás. ` +
      `Van de a ${MAX_PAGINAS_IMAGEN}, así que pedí las que necesites, no todas.`
    );
  }
  const completo = p.textoPorPagina.join('\n\n').trim();
  const muestra = completo.slice(0, CARACTERES_MUESTRA);
  const corto = muestra.length >= completo.length;
  return [
    `${cabeza} Tiene capa de texto.${corto ? ' Va COMPLETO acá abajo.' : ' Acá va el principio; para el resto, ver_pdf.'}`,
    '',
    muestra,
    corto ? '' : `\n[…siguen ${completo.length - muestra.length} caracteres más]`,
  ]
    .filter((l) => l !== '')
    .join('\n');
}

// ── ver_pdf ─────────────────────────────────────────────────────────────────

export interface VentanaPaginas {
  desde: number;
  cantidad: number;
  /** true = hay que renderizar imágenes en vez de devolver texto. */
  comoImagen: boolean;
}

/**
 * Qué páginas pidió el modelo. Si el PDF no tiene texto, la respuesta es SIEMPRE imagen
 * aunque haya pedido texto: devolverle un string vacío lo haría concluir que el documento
 * está en blanco.
 */
export function ventanaDePaginas(p: Pdf | null, entrada: unknown): VentanaPaginas {
  const o = (entrada ?? {}) as { desde?: unknown; paginas?: unknown; como?: unknown };
  const comoImagen = !p?.tieneTexto || texto(o.como).toLowerCase() === 'imagen';
  const max = comoImagen ? MAX_PAGINAS_IMAGEN : MAX_PAGINAS_TEXTO;
  return {
    desde: Math.max(entero(o.desde) ?? 1, 1),
    cantidad: Math.min(Math.max(entero(o.paginas) ?? max, 1), max),
    comoImagen,
  };
}

/** El texto que devuelve `ver_pdf` cuando el PDF tiene capa de texto. */
export function lecturaDePdf(p: Pdf | null, v: VentanaPaginas): string {
  if (!p) {
    return 'No hay ningún PDF adjunto en este chat. Pedile al usuario que lo adjunte con el "+".';
  }
  if (v.desde > p.paginas) {
    return `Ese tramo no existe: el PDF tiene ${p.paginas} página(s). Pedí un \`desde\` entre 1 y ${p.paginas}.`;
  }
  const hasta = Math.min(p.paginas, v.desde + v.cantidad - 1);
  const partes: string[] = [];
  for (let i = v.desde; i <= hasta; i++) {
    const t = (p.textoPorPagina[i - 1] ?? '').trim();
    partes.push(`── Página ${i} ──\n${t || '(sin texto en esta página)'}`);
  }
  const quedan = p.paginas - hasta;
  partes.push(
    quedan > 0
      ? `(quedan ${quedan} página(s); para seguir, ver_pdf con desde=${hasta + 1})`
      : '(es el final del documento)',
  );
  return partes.join('\n\n');
}

/** El encabezado que acompaña a las páginas renderizadas. */
export function encabezadoDeImagenes(p: Pdf, v: VentanaPaginas, cuantas: number): string {
  const hasta = v.desde + cuantas - 1;
  const quedan = p.paginas - hasta;
  return (
    `Páginas ${v.desde} a ${hasta} de ${p.paginas} de "${p.nombre}", renderizadas como imagen ` +
    `(el PDF no tiene capa de texto). Leelas de la imagen.` +
    (quedan > 0 ? ` Quedan ${quedan}: para seguir, ver_pdf con desde=${hasta + 1}.` : '')
  );
}

// ── crear_pdf ───────────────────────────────────────────────────────────────

/** Una foto sacada de un PDF, ya subida, con el número que lleva en la hoja de contacto. */
export interface FotoDelPdfSubida {
  numero: number;
  id: string;
  pagina: number;
}

/** Páginas pedidas a `extraer_fotos_del_pdf` (enteros ≥ 1, sin repetir); vacío = todas. */
export function paginasPedidas(entrada: unknown): number[] {
  const crudas = (entrada as { paginas?: unknown } | null)?.paginas;
  if (!Array.isArray(crudas)) return [];
  return [...new Set(crudas.map(entero).filter((n): n is number => n !== null && n >= 1))].sort((a, b) => a - b);
}

/**
 * El texto que acompaña a las hojas de contacto (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf).
 * Va PRIMERO porque es lo que queda en el registro del historial: los ids tienen que sobrevivir a
 * los turnos siguientes, las hojas no.
 */
export function textoDeFotosDelPdf(nombre: string, fotos: FotoDelPdfSubida[], descartadas: number, hojas: number): string {
  if (fotos.length === 0) {
    return `El PDF "${nombre}" no tiene fotos adentro${descartadas ? ` (sólo ${descartadas} logos, íconos o repetidas)` : ''}. Si está escaneado, las páginas son la foto: miralas con ver_pdf (como: "imagen").`;
  }
  const lista = fotos.map((f) => `${f.numero}=${f.id} (p${f.pagina})`).join(', ');
  return (
    `Fotos del PDF "${nombre}": ${fotos.length}${descartadas ? ` (salteé ${descartadas}: logos, íconos, repetidas o pasadas del tope de 100; si falta una, pedí esas \`paginas\`)` : ''}. ` +
    `Van en ${hojas} hoja${hojas === 1 ? '' : 's'} numerada${hojas === 1 ? '' : 's'} ("n · pX" = número y página). ` +
    `Cada número es un adjunto_id: ${lista}. ` +
    'Se usan como cualquier adjunto: reference_images con source "adjunto", agregar_productos con adjunto_ids, ver_fotos para verla grande.'
  );
}

export interface PdfCreado {
  nombre: string;
  titulo: string;
  bloques: BloquePdf[];
  /** Color de acento (hex). */
  color?: string;
  /** Línea del pie, además del número de página. */
  pie?: string;
  /** Línea chica arriba a la derecha de cada página salvo la primera. */
  encabezado?: string;
  /** URLs de las imágenes referenciadas, por ref. Se bajan al descargar, no antes:
   *  un documento que nadie baja no cuesta ni una descarga
   *  (ADR-2026-09-08-el-pdf-con-estilos-e-imagenes). */
  imagenes: Record<string, string>;
}

/** Los bloques que el agente puede pedir (el nombre del `tipo`). Es la misma lista que
 *  nombra la herramienta y la skill; `pdf.test.ts` lo verifica. */
export const TIPOS_BLOQUE = [
  'portada',
  'banda',
  'titulo',
  'subtitulo',
  'parrafo',
  'nota',
  'lista',
  'tabla',
  'destacado',
  'cifras',
  'tarjetas',
  'ficha',
  'columnas',
  'etiquetas',
  'imagen',
  'separador',
] as const;

const ALINEACIONES = ['izquierda', 'centro', 'derecha'];

/** Tope de imágenes por documento: cada una son ~200 KB en el archivo. */
export const MAX_IMAGENES_PDF = 30;
/** Topes de ítems por bloque compuesto: más que esto no entra en una hoja legible. */
export const MAX_TARJETAS = 24;
export const MAX_CIFRAS = 8;
export const MAX_ETIQUETAS = 24;

/** Un color sólo si es un hex de 6 dígitos; cualquier otra cosa se ignora. */
const colorValido = (v: unknown): string | undefined => {
  const t = texto(v).trim();
  return /^#?[0-9a-f]{6}$/i.test(t) ? (t.startsWith('#') ? t : `#${t}`) : undefined;
};

/** Nombre de archivo seguro, sin barras ni extensión duplicada. */
export function nombreDeArchivoPdf(nombre: unknown): string {
  const base =
    texto(nombre)
      .replace(/\.pdf$/i, '')
      .replace(/[/\\:*?"<>|]/g, '-')
      .trim()
      .slice(0, 80) || 'documento';
  return `${base}.pdf`;
}

/**
 * Lo que escribió el modelo → un documento válido. Nunca tira: un bloque que no se entiende
 * se descarta y se sigue, porque un error acá dejaría al usuario sin archivo después de que
 * el agente le dijo que se lo estaba armando.
 */
export function normalizarPdfCreado(entrada: unknown): PdfCreado {
  const o = (entrada ?? {}) as { nombre?: unknown; titulo?: unknown; bloques?: unknown };
  const crudos = Array.isArray(o.bloques) ? o.bloques : [];
  const bloques: BloquePdf[] = [];
  const imagenes: Record<string, string> = {};
  // Una URL de imagen → su ref, respetando el tope; `undefined` si no entra.
  const refDe = (url: unknown): string | undefined => {
    const u = texto(url).trim();
    if (!u || Object.keys(imagenes).length >= MAX_IMAGENES_PDF) return undefined;
    const ref = `img${Object.keys(imagenes).length + 1}`;
    imagenes[ref] = u;
    return ref;
  };
  const corto = (v: unknown, max: number) => texto(v).trim().slice(0, max);
  for (const b of crudos) {
    if (!b || typeof b !== 'object') continue;
    const bo = b as Record<string, unknown>;
    const tipo = texto(bo.tipo).toLowerCase();
    if (!(TIPOS_BLOQUE as readonly string[]).includes(tipo)) continue;
    const align = ALINEACIONES.includes(texto(bo.align))
      ? (texto(bo.align) as Alineacion)
      : undefined;
    const color = colorValido(bo.color);
    if (tipo === 'separador') {
      bloques.push({ tipo: 'separador' });
    } else if (tipo === 'imagen') {
      // Una imagen sin URL no es nada, y pasado el tope se ignoran las que sobran: el
      // archivo se vuelve inmanejable y el navegador tiene que bajarlas todas.
      const ref = refDe(bo.url);
      if (!ref) continue;
      const alto = Number(bo.alto);
      bloques.push({
        tipo: 'imagen',
        ref,
        ...(Number.isFinite(alto) && alto > 0 ? { alto: Math.min(600, alto) } : {}),
        ...(align ? { align } : {}),
      });
    } else if (tipo === 'portada') {
      const t = corto(bo.texto, 160);
      if (!t) continue;
      bloques.push({
        tipo: 'portada',
        texto: t,
        ...(corto(bo.subtexto, 240) ? { subtexto: corto(bo.subtexto, 240) } : {}),
        ...(corto(bo.detalle, 120) ? { detalle: corto(bo.detalle, 120) } : {}),
        ...(color ? { color } : {}),
      });
    } else if (tipo === 'banda') {
      const t = corto(bo.texto, 120);
      if (t) bloques.push({ tipo: 'banda', texto: t, ...(color ? { color } : {}) });
    } else if (tipo === 'destacado') {
      const t = texto(bo.texto).trim();
      if (t) bloques.push({ tipo: 'destacado', texto: t, ...(color ? { color } : {}) });
    } else if (tipo === 'nota') {
      const t = texto(bo.texto).trim();
      if (t) bloques.push({ tipo: 'nota', texto: t, ...(align ? { align } : {}) });
    } else if (tipo === 'lista') {
      const items = (Array.isArray(bo.items) ? bo.items : []).map(texto).filter(Boolean);
      if (items.length > 0) bloques.push({ tipo: 'lista', items });
    } else if (tipo === 'etiquetas') {
      const items = (Array.isArray(bo.items) ? bo.items : [])
        .map((x) => corto(x, 40))
        .filter(Boolean)
        .slice(0, MAX_ETIQUETAS);
      if (items.length > 0) bloques.push({ tipo: 'etiquetas', items, ...(color ? { color } : {}) });
    } else if (tipo === 'cifras') {
      const items = (Array.isArray(bo.items) ? bo.items : [])
        .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
        .map((x) => ({ valor: corto(x.valor, 24), etiqueta: corto(x.etiqueta, 60) }))
        .filter((x) => x.valor || x.etiqueta)
        .slice(0, MAX_CIFRAS);
      if (items.length > 0) bloques.push({ tipo: 'cifras', items });
    } else if (tipo === 'tarjetas') {
      const items = (Array.isArray(bo.items) ? bo.items : [])
        .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
        .slice(0, MAX_TARJETAS)
        .map((x) => {
          const ref = refDe(x.url);
          return {
            ...(corto(x.titulo, 120) ? { titulo: corto(x.titulo, 120) } : {}),
            ...(corto(x.texto, 400) ? { texto: corto(x.texto, 400) } : {}),
            ...(corto(x.cifra, 24) ? { cifra: corto(x.cifra, 24) } : {}),
            ...(ref ? { ref } : {}),
          };
        })
        .filter((x) => x.titulo || x.texto || x.cifra || x.ref);
      if (items.length === 0) continue;
      const columnas = Number(bo.columnas);
      bloques.push({
        tipo: 'tarjetas',
        items,
        ...(columnas === 2 || columnas === 3 ? { columnas } : {}),
      });
    } else if (tipo === 'ficha') {
      const ref = refDe(bo.url);
      const titulo = corto(bo.titulo, 120);
      const cuerpo = corto(bo.texto, 900);
      const detalle = corto(bo.detalle, 60);
      if (!ref && !titulo && !cuerpo) continue;
      bloques.push({
        tipo: 'ficha',
        ...(ref ? { ref } : {}),
        ...(titulo ? { titulo } : {}),
        ...(cuerpo ? { texto: cuerpo } : {}),
        ...(detalle ? { detalle } : {}),
        ...(texto(bo.lado) === 'derecha' ? { lado: 'derecha' as const } : {}),
      });
    } else if (tipo === 'columnas') {
      const izquierda = texto(bo.izquierda).trim();
      const derecha = texto(bo.derecha).trim();
      if (!izquierda && !derecha) continue;
      const titulos = Array.isArray(bo.titulos) ? bo.titulos.map((t) => corto(t, 80)) : [];
      bloques.push({
        tipo: 'columnas',
        izquierda,
        derecha,
        ...(titulos.some(Boolean) ? { titulos: [titulos[0] ?? '', titulos[1] ?? ''] as [string, string] } : {}),
      });
    } else if (tipo === 'tabla') {
      const filas = (Array.isArray(bo.filas) ? bo.filas : [])
        .filter((f): f is unknown[] => Array.isArray(f))
        .map((f) => f.map(texto));
      if (filas.length > 0) {
        bloques.push({
          tipo: 'tabla',
          filas,
          ...(texto(bo.estilo) === 'rayada' ? { estilo: 'rayada' as const } : {}),
          ...(texto(bo.encabezado) === 'solido' ? { encabezado: 'solido' as const } : {}),
        });
      }
    } else {
      const t = texto(bo.texto).trim();
      if (t) {
        bloques.push({
          tipo: tipo as 'titulo' | 'subtitulo' | 'parrafo',
          texto: t,
          ...(align ? { align } : {}),
          ...(color ? { color } : {}),
        });
      }
    }
  }
  const titulo = texto(o.titulo).trim();
  const oo = o as { color?: unknown; pie?: unknown; encabezado?: unknown };
  return {
    nombre: nombreDeArchivoPdf(o.nombre || titulo),
    titulo,
    bloques,
    imagenes,
    ...(colorValido(oo.color) ? { color: colorValido(oo.color)! } : {}),
    ...(texto(oo.pie).trim() ? { pie: texto(oo.pie).trim().slice(0, 120) } : {}),
    ...(texto(oo.encabezado).trim() ? { encabezado: texto(oo.encabezado).trim().slice(0, 120) } : {}),
  };
}

/** Un vistazo de lo que tiene el documento, para la card y para el agente. */
export function contarBloques(p: PdfCreado): {
  bloques: number;
  tablas: number;
  palabras: number;
  imagenes: number;
} {
  let palabras = 0;
  let tablas = 0;
  const cuenta = (t: string | undefined) => (t && t.trim() ? t.trim().split(/\s+/).length : 0);
  for (const b of p.bloques) {
    if (b.tipo === 'tabla') {
      tablas += 1;
      palabras += b.filas.reduce((n, f) => n + cuenta(f.join(' ')), 0);
    } else if (b.tipo === 'lista' || b.tipo === 'etiquetas') {
      palabras += cuenta(b.items.join(' '));
    } else if (b.tipo === 'cifras') {
      palabras += b.items.reduce((n, c) => n + cuenta(`${c.valor} ${c.etiqueta}`), 0);
    } else if (b.tipo === 'tarjetas') {
      palabras += b.items.reduce((n, t) => n + cuenta(`${t.cifra ?? ''} ${t.titulo ?? ''} ${t.texto ?? ''}`), 0);
    } else if (b.tipo === 'ficha') {
      palabras += cuenta(`${b.titulo ?? ''} ${b.texto ?? ''} ${b.detalle ?? ''}`);
    } else if (b.tipo === 'columnas') {
      palabras += cuenta(`${b.titulos?.join(' ') ?? ''} ${b.izquierda} ${b.derecha}`);
    } else if (b.tipo === 'portada') {
      palabras += cuenta(`${b.texto} ${b.subtexto ?? ''} ${b.detalle ?? ''}`);
    } else if (b.tipo === 'separador' || b.tipo === 'imagen') {
      /* sin texto */
    } else {
      palabras += cuenta(b.texto);
    }
  }
  return {
    bloques: p.bloques.length,
    tablas,
    palabras,
    imagenes: Object.keys(p.imagenes).length,
  };
}

/** El `[registro:]` del historial. */
export function registroPdfParaHistorial(p: PdfCreado): string {
  const { bloques, palabras, imagenes } = contarBloques(p);
  const fotos = imagenes > 0 ? `, ${imagenes} imagen(es)` : '';
  return `[registro: armaste el PDF "${p.nombre}"${p.titulo ? ` (título: "${p.titulo}")` : ''} con ${bloques} bloque(s), ~${palabras} palabras${fotos}. Ya está en el chat para descargar.]`;
}
